//authService.jsx
import { getAuth, onAuthStateChanged, signInWithPopup, signOut } from "firebase/auth";
import { t } from "i18next";
import { auth, facebookProvider, googleProvider } from "../utils/firebaseConfig";

/**
 * Signs in with the given provider (Google or Facebook) using Firebase.
 * @param {object} provider - Firebase auth provider.
 * @returns {Promise<Object>} The authenticated user data.
 */
const signInWithProvider = async (provider) => {
  try {
    const result = await signInWithPopup(auth, provider);
    let signedInUser = result.user;

    if (!signedInUser) throw new Error(t("errors.authFailed"));

    let userData = {
      uid: signedInUser.uid,
      email: signedInUser.email || "",
      displayName: signedInUser.displayName,
      photoURL: signedInUser.photoURL,
    };

    console.info(t("logs.signInSuccess", { provider: signedInUser.providerId }));
    return userData;
  } catch (error) {
    console.error(t("logs.signInError", { provider: provider.providerId }), error);
    throw new Error(t("errors.authFailed"));
  }
};

export const googleSignIn = () => signInWithProvider(googleProvider);
export const facebookSignIn = () => signInWithProvider(facebookProvider);

/**
 * Logs out the user and redirects to the login page.
 * @param {Function} navigate - Navigation function (e.g., from react-router-dom)
 */
export const logout = async (navigate) => {
  try {
    await signOut(auth);
    navigate("/recipes");
    console.log(`${t("logs.logoutSuccess")}`);
  } catch (error) {
    console.error(`${t("logs.logoutError")}:`, error);
    throw new Error(t("errors.logoutFailed"));
  }
};

/**
 * Observes user authentication state.
 * @param {Function} setUser - Function to update user state.
 */
export const observeAuthState = (setUser) => {
  return onAuthStateChanged(auth, (user) => setUser(user));
};

/**
 * Retrieves Firebase authentication token.
 * @returns {Promise<string>} - The Firebase token.
 */
export const getFirebaseToken = async () => {
  const authInstance = getAuth();
  const user = authInstance.currentUser;

  try {
    return await user.getIdToken(true);
  } catch (error) {
    console.error(`${t("logs.tokenError")}:`, error);
    throw new Error(t("errors.failedToRetrieveToken"));
  }
};

/**
 * Retrieves the current user from Firebase authentication.
 *
 * @returns {object|null} The current user object or null.
 */
export const getCurrentUser = () => {
  return getAuth().currentUser;
};
